import { AppWindow, Eye, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import notify from '../../lib/notify';
import { extractApiErrorMessage } from '../../lib/apiError';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../Common/button';
import { Badge } from '../Common/badge';
import Modal from '../Common/Modal';
import ConfirmationModal from '../Common/ConfirmationModal';
import TableSkeleton from '../Common/TableSkeleton';
import { ApplicationCreationWizard, OneApplicationModule } from '../Forge';
import {
  Forge,
  ForgeApplication,
  ForgeApplicationWithContext,
  ForgeClient,
  UpdateStandaloneAppRequest,
} from '../../types';

type EditFormState = {
  status: string;
  description: string;
};

const getStatusBadgeVariant = (status?: string | null): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
    case 'active':
      return 'default';
    case 'stopped':
    case 'initializing':
    case 'creating':
      return 'secondary';
    case 'error':
    case 'build_failed':
      return 'destructive';
    default:
      return 'outline';
  }
};

const formatAppType = (t?: string) => (t ? t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');

export const ApplicationsTab: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.user);
  const createdByDefault = currentUser?.email || 'super-admin';

  const [apps, setApps] = useState<ForgeApplicationWithContext[]>([]);
  const [forges, setForges] = useState<Forge[]>([]);
  const [clients, setClients] = useState<ForgeClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);

  const [editApp, setEditApp] = useState<ForgeApplication | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({ status: '', description: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleteApp, setDeleteApp] = useState<ForgeApplication | null>(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [viewApp, setViewApp] = useState<ForgeApplicationWithContext | null>(null);

  const clientById = useMemo(() => {
    const m = new Map<string, ForgeClient>();
    clients.forEach((c) => m.set(c.client_id, c));
    return m;
  }, [clients]);

  const forgeById = useMemo(() => {
    const m = new Map<string, Forge>();
    forges.forEach((f) => m.set(f.forge_id, f));
    return m;
  }, [forges]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, f, c] = await Promise.all([
        forgeService.listApplicationsWithContext(),
        forgeService.listForges(),
        forgeService.listClients(),
      ]);
      setApps(a);
      setForges(f);
      setClients(c);
    } catch (err) {
      console.error('Failed to load applications', err);
      setError(t('forgeDashboard.applications.loadError', 'Failed to load applications'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (a: ForgeApplication) => {
    setEditApp(a);
    const appWithDesc = a as ForgeApplication & { description?: string | null };
    setEditForm({ status: a.status || '', description: appWithDesc.description || '' });
  };

  const handleUpdate = async () => {
    if (!editApp) return;
    setEditSubmitting(true);
    try {
      const payload: UpdateStandaloneAppRequest = {
        status: editForm.status || null,
        description: editForm.description || null,
      };
      await forgeService.updateStandaloneApplication(
        editApp.client_id,
        editApp.forge_id,
        editApp.app_id,
        payload
      );
      notify.success(t('forgeDashboard.applications.updateSuccess', 'Application updated'));
      setEditApp(null);
      fetchAll();
    } catch (err) {
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.applications.updateError', 'Failed to update application')));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteApp) return;
    setDeleteSubmitting(true);
    try {
      await forgeService.deleteStandaloneApplication(
        deleteApp.client_id,
        deleteApp.forge_id,
        deleteApp.app_id
      );
      notify.success(t('forgeDashboard.applications.deleteSuccess', 'Application deleted'));
      setDeleteApp(null);
      setDeleteInput('');
      fetchAll();
    } catch (err) {
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.applications.deleteError', 'Failed to delete application')));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <AppWindow className="h-4 w-4" />
          <span>
            {t('forgeDashboard.applications.summary', '{{count}} applications', { count: apps.length })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('global.refresh', 'Refresh')}
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('forgeDashboard.applications.new', 'New application')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-destructive">{error}</div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <TableSkeleton rows={6} cols={6} />
          ) : apps.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {t('forgeDashboard.applications.empty', 'No applications yet')}
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.applications.name', 'Name')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('global.status', 'Status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.applications.forge', 'Forge')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.applications.client', 'Client')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.applications.link', 'Use case')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {apps.map((a) => {
                  const forge = forgeById.get(a.forge_id);
                  const client = clientById.get(a.client_id);
                  const isStandalone = !a.use_case_id;
                  return (
                    <tr key={a.app_id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-foreground">{a.name}</div>
                        <div className="text-xs text-muted-foreground">{formatAppType(a.app_type)}</div>
                      </td>
                      <td className="px-4 py-3">
                        {a.status && <Badge variant={getStatusBadgeVariant(a.status)}>{a.status}</Badge>}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">
                        {forge?.display_name || forge?.name || a.forge_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{client?.name || a.client_id}</td>
                      <td className="px-4 py-3 text-sm">
                        {isStandalone ? (
                          <Badge variant="outline">{t('forgeDashboard.applications.standalone', 'Standalone')}</Badge>
                        ) : (
                          <span className="text-foreground/80 truncate max-w-[160px] inline-block" title={a.use_case_name || ''}>
                            {a.use_case_name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setViewApp(a)} className="gap-1.5">
                            <Eye className="h-3.5 w-3.5" />
                            {t('global.view', 'View')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openEdit(a)} className="gap-1.5">
                            <Pencil className="h-3.5 w-3.5" />
                            {t('global.edit', 'Edit')}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => { setDeleteApp(a); setDeleteInput(''); }}
                            className="gap-1.5"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            {t('global.delete', 'Delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create wizard (3-step: overview → template → summary) */}
      <ApplicationCreationWizard
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        mode={{ kind: 'standalone', clients, forges }}
        currentUserEmail={createdByDefault}
        onCreated={() => fetchAll()}
      />

      {/* Edit modal */}
      <Modal
        open={!!editApp}
        title={t('forgeDashboard.applications.editTitle', 'Edit application')}
        onClose={() => setEditApp(null)}
        hideDefaultFooter
        footer={
          <>
            <Button variant="outline" onClick={() => setEditApp(null)} disabled={editSubmitting}>
              {t('global.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleUpdate} disabled={editSubmitting}>
              {editSubmitting ? t('global.saving', 'Saving…') : t('global.save', 'Save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('global.status', 'Status')}</label>
            <input
              className={inputCls}
              value={editForm.status}
              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              placeholder="deployed, stopped, …"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.applications.description', 'Description')}
            </label>
            <textarea
              className={inputCls}
              rows={3}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
          </div>
        </div>
      </Modal>

      {/* View modal */}
      <Modal
        open={!!viewApp}
        title={
          viewApp
            ? t('forgeDashboard.applications.viewTitle', 'Application: {{name}}', { name: viewApp.name })
            : ''
        }
        onClose={() => setViewApp(null)}
        className="max-w-5xl"
        bodyClassName="max-h-[80vh] overflow-y-auto"
      >
        {viewApp && (
          <OneApplicationModule
            applicationId={viewApp.app_id}
            useCaseId={viewApp.use_case_id}
            useCaseName={viewApp.use_case_name}
            cadrageId={viewApp.cadrage_id}
            cadrageName={viewApp.cadrage_name}
            showHeader={false}
            showReadme={true}
          />
        )}
      </Modal>

      {/* Delete confirmation */}
      <ConfirmationModal
        open={!!deleteApp}
        title={t('forgeDashboard.applications.deleteTitle', 'Delete application?')}
        message={t(
          'forgeDashboard.applications.deleteMessage',
          'This will permanently delete application "{{name}}" from Forge. This cannot be undone.',
          { name: deleteApp?.name || '' }
        )}
        confirmText={t('global.delete', 'Delete')}
        cancelText={t('global.cancel', 'Cancel')}
        variant="danger"
        loading={deleteSubmitting}
        requireTextMatch={
          deleteApp
            ? { text: deleteApp.name, label: t('forgeDashboard.confirmTypeName', 'Type the name to confirm') }
            : undefined
        }
        inputValue={deleteInput}
        onInputChange={setDeleteInput}
        onConfirm={handleDelete}
        onCancel={() => { setDeleteApp(null); setDeleteInput(''); }}
      />
    </div>
  );
};

export default ApplicationsTab;
